import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { validateWordList, WORD_MAX, DEF_MAX } from '../utils/validateWordList';
import { parseText } from '../utils/parseWordList';

export default function CustomListNewPage() {
  const { isSubscriber } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('manual'); // 'manual' | 'file' | 'paste'
  const [listName, setListName] = useState('');
  const [step, setStep] = useState('edit'); // 'edit' | 'preview'

  // Manual entry
  const [manualRows, setManualRows] = useState(
    Array.from({ length: 5 }, () => ({ word: '', definition: '' }))
  );

  // File upload
  const [fileName, setFileName] = useState('');
  const [fileParsed, setFileParsed] = useState(null);

  // Paste
  const [pasteText, setPasteText] = useState('');
  const [pasteParsed, setPasteParsed] = useState(null);

  const [validationErrors, setValidationErrors] = useState([]);
  const [previewWarnings, setPreviewWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Show upgrade prompt for non-subscribers
  if (!isSubscriber) {
    return (
      <DashboardLayout>
        <div style={s.header}>
          <h1 style={s.heading}>New Custom List</h1>
        </div>
        <div style={s.upgradeCard}>
          <p style={s.upgradeHeading}>Subscriber feature</p>
          <p style={s.upgradeText}>
            Custom vocabulary uploads are available to subscribers. Subscribe for $19.99/year
            to upload your own word lists and reuse them across game sessions.
          </p>
          <Link to="/subscribe" style={s.upgradeBtn}>Subscribe for $19.99/yr →</Link>
        </div>
      </DashboardLayout>
    );
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function getWords() {
    if (tab === 'manual') {
      return manualRows.filter(r => r.word.trim() && r.definition.trim());
    }
    if (tab === 'file') return fileParsed || [];
    return pasteParsed || [];
  }

  function addManualRow() {
    setManualRows(r => [...r, { word: '', definition: '' }]);
  }

  function updateManual(idx, field, value) {
    setManualRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  }

  function removeManualRow(idx) {
    setManualRows(r => r.filter((_, i) => i !== idx));
  }

  function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = evt => {
      const text = evt.target.result;
      const lines = text.trim().split('\n');
      const firstLine = lines[0].toLowerCase().trim();
      // Skip header row if it looks like "word,definition" or "word\tdefinition"
      // The first field must be exactly "word", so an entry like "wordy" is kept.
      const firstField = firstLine.split(/[,\t]/)[0].trim().replace(/^"|"$/g, '');
      const textToUse = (firstField === 'word' &&(firstLine.includes(',') || firstLine.includes('\t')))
        ? lines.slice(1).join('\n')
        : text;
      setFileParsed(parseText(textToUse));
    };
    reader.readAsText(file);
  }

  function handleParsePaste() {
    setPasteParsed(parseText(pasteText));
  }

  // ── Preview / validate ───────────────────────────────────────────────────────

  function handlePreview() {
    const words = getWords();
    const { errors, warnings } = validateWordList(listName, words);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setPreviewWarnings([]);
      return;
    }
    setValidationErrors([]);
    setPreviewWarnings(warnings);
    setStep('preview');
  }

  // ── Save ─────────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaveError('');
    setSaving(true);
    const words = getWords();
    let res;
    let data;
    try {
      res = await fetch('/api/teacher/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: listName.trim(), words }),
      });
      data = await res.json();
    } catch {
      setSaveError('Could not reach the server. Check your connection and try again.');
      setSaving(false);
      return;
    }
    if (res.ok) {
      navigate('/lists');
    } else {
      setSaveError(data.error || 'Failed to save. Please try again.');
      setSaving(false);
    }
  }

  // ── Preview step ─────────────────────────────────────────────────────────────

  if (step === 'preview') {
    const words = getWords();
    return (
      <DashboardLayout>
        <div style={s.header}>
          <h1 style={s.heading}>Preview: {listName}</h1>
          <button onClick={() => setStep('edit')} style={s.backBtn}>← Back to Edit</button>
        </div>

        {previewWarnings.length > 0 && (
          <div style={s.warningBox}>
            {previewWarnings.map((w, i) => <p key={i} style={s.warningText}>{w}</p>)}
          </div>
        )}

        {saveError && <div style={s.errorBox}><p style={s.errorText}>{saveError}</p></div>}

        <div style={s.card}>
          <p style={s.previewCount}>{words.length} words ready to save</p>
          <table style={s.manualTable}>
            <thead>
              <tr>
                <th style={s.th}>#</th>
                <th style={s.th}>Word</th>
                <th style={s.th}>Definition</th>
              </tr>
            </thead>
            <tbody>
              {words.map((row, i) => (
                <tr key={i}>
                  <td style={{ ...s.manualTd, color: '#9ca3af', fontSize: '12px', width: '32px' }}>{i + 1}</td>
                  <td style={s.manualTd}>{row.word}</td>
                  <td style={s.manualTd}>{row.definition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={s.previewActions}>
          <button onClick={() => setStep('edit')} style={s.cancelBtn}>Back to Edit</button>
          <button onClick={handleSave} disabled={saving} style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}>
            {saving ? 'Saving…' : 'Confirm & Save List'}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // ── Edit step ────────────────────────────────────────────────────────────────

  const words = getWords();

  return (
    <DashboardLayout>
      <div style={s.header}>
        <h1 style={s.heading}>New Custom List</h1>
        <Link to="/lists" style={s.backLink}>← My Lists</Link>
      </div>

      {/* List name */}
      <div style={s.card}>
        <label style={s.label}>List Name</label>
        <input
          value={listName}
          onChange={e => setListName(e.target.value)}
          placeholder="e.g. Chapter 1–5 Vocabulary"
          style={{ ...s.input, maxWidth: '400px' }}
        />
      </div>

      {/* Tab switcher */}
      <div style={s.tabs}>
        {[
          ['manual', 'Manual Entry'],
          ['file',   'Upload File'],
          ['paste',  'Paste Text'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* Manual entry */}
      {tab === 'manual' && (
        <div style={s.card}>
          <p style={s.hint}>
            Word limit: {WORD_MAX} characters. Definition limit: {DEF_MAX} characters.
          </p>
          <table style={s.manualTable}>
            <thead>
              <tr>
                <th style={s.th}>Word</th>
                <th style={s.th}>Definition</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {manualRows.map((row, idx) => (
                <tr key={idx}>
                  <td style={s.manualTd}>
                    <input
                      value={row.word}
                      onChange={e => updateManual(idx, 'word', e.target.value)}
                      placeholder="Word"
                      maxLength={WORD_MAX}
                      style={{ ...s.cellInput, borderColor: row.word.trim().length > WORD_MAX ? '#dc2626' : undefined }}
                    />
                  </td>
                  <td style={s.manualTd}>
                    <input
                      value={row.definition}
                      onChange={e => updateManual(idx, 'definition', e.target.value)}
                      placeholder="Definition"
                      maxLength={DEF_MAX}
                      style={{ ...s.cellInput, width: '100%', borderColor: row.definition.trim().length > DEF_MAX ? '#dc2626' : undefined }}
                    />
                  </td>
                  <td style={s.manualTd}>
                    <button onClick={() => removeManualRow(idx)} style={s.removeBtn}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={addManualRow} style={s.addRowBtn}>+ Add Row</button>
        </div>
      )}

      {/* File upload */}
      {tab === 'file' && (
        <div style={s.card}>
          <p style={s.hint}>
            Upload a CSV or plain text file. One word/definition pair per line, separated by a comma or tab.
            First row is auto-detected as a header and skipped.{' '}
            <a href="/api/teacher/lists/template.csv" style={s.templateLink}>Download CSV template</a>
          </p>
          <label style={s.fileLabel}>
            <input type="file" accept=".csv,.txt" onChange={handleFileUpload} style={{ display: 'none' }} />
            <span style={s.fileBtn}>Choose file</span>
            {fileName && <span style={s.fileName}>{fileName}</span>}
          </label>

          {fileParsed !== null && (
            <div style={{ marginTop: '16px' }}>
              <p style={s.hint}>{fileParsed.length} pairs parsed from file.</p>
              {fileParsed.length > 0 && (
                <table style={s.manualTable}>
                  <thead>
                    <tr>
                      <th style={s.th}>Word</th>
                      <th style={s.th}>Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileParsed.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        <td style={s.manualTd}>{row.word}</td>
                        <td style={s.manualTd}>{row.definition}</td>
                      </tr>
                    ))}
                    {fileParsed.length > 10 && (
                      <tr>
                        <td colSpan={2} style={{ ...s.manualTd, color: '#6b6b7a', fontStyle: 'italic' }}>
                          … and {fileParsed.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Paste text */}
      {tab === 'paste' && (
        <div style={s.card}>
          <p style={s.hint}>
            Paste comma or tab-separated word/definition pairs, one per line.{' '}
            <a href="/api/teacher/lists/template.csv" style={s.templateLink}>Download CSV template</a>
          </p>
          <textarea
            value={pasteText}
            onChange={e => { setPasteText(e.target.value); setPasteParsed(null); }}
            placeholder={'word, definition\nword2, definition2'}
            style={s.textarea}
          />
          <button onClick={handleParsePaste} style={s.parseBtn}>Parse →</button>

          {pasteParsed !== null && (
            <div style={{ marginTop: '16px' }}>
              <p style={s.hint}>{pasteParsed.length} pairs parsed.</p>
              {pasteParsed.length > 0 && (
                <table style={s.manualTable}>
                  <thead>
                    <tr>
                      <th style={s.th}>Word</th>
                      <th style={s.th}>Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pasteParsed.slice(0, 10).map((row, i) => (
                      <tr key={i}>
                        <td style={s.manualTd}>{row.word}</td>
                        <td style={s.manualTd}>{row.definition}</td>
                      </tr>
                    ))}
                    {pasteParsed.length > 10 && (
                      <tr>
                        <td colSpan={2} style={{ ...s.manualTd, color: '#6b6b7a', fontStyle: 'italic' }}>
                          … and {pasteParsed.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div style={s.errorBox}>
          <p style={{ ...s.errorText, fontWeight: '700', marginBottom: '6px' }}>
            Please fix the following before continuing:
          </p>
          <ul style={s.errorList}>
            {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Footer bar */}
      <div style={s.summaryBar}>
        <span style={{ color: words.length >= 40 ? '#15803d' : words.length >= 25 ? '#d97706' : '#dc2626', fontWeight: '600' }}>
          {words.length} word{words.length !== 1 ? 's' : ''}
          {words.length < 25 && ` (need at least 25 to save, enough for 4×4)`}
          {words.length >= 25 && words.length < 40 && ' ✓ (need 40 for 5×5)'}
          {words.length >= 40 && ' ✓'}
        </span>
        <button
          onClick={handlePreview}
          style={{ ...s.saveBtn, marginLeft: 'auto' }}
        >
          Preview & Validate →
        </button>
      </div>
    </DashboardLayout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  heading: { fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: 0 },
  backLink: { fontSize: '14px', color: '#1a1a2e', fontWeight: '600', textDecoration: 'none' },
  backBtn: { padding: '8px 16px', background: 'none', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', cursor: 'pointer', color: '#374151', fontWeight: '500' },
  upgradeCard: { backgroundColor: '#fff', border: '1px solid #e5e0d5', borderRadius: '12px', padding: '32px' },
  upgradeHeading: { fontWeight: '700', fontSize: '17px', color: '#1a1a2e', margin: '0 0 8px' },
  upgradeText: { fontSize: '14px', color: '#6b6b7a', margin: '0 0 16px', lineHeight: '1.6' },
  upgradeBtn: { display: 'inline-block', padding: '10px 20px', backgroundColor: '#1a1a2e',
    color: '#fff', borderRadius: '8px', fontWeight: '600', fontSize: '14px', textDecoration: 'none' },
  card: { backgroundColor: '#fff', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '20px 24px', marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  tabs: { display: 'flex', gap: '0', borderBottom: '2px solid #e5e0d5', marginBottom: '0' },
  tab: { padding: '10px 20px', background: 'none', border: 'none', fontSize: '14px',
    fontWeight: '500', color: '#6b6b7a', cursor: 'pointer', borderBottom: '2px solid transparent',
    marginBottom: '-2px' },
  tabActive: { color: '#1a1a2e', fontWeight: '700', borderBottom: '2px solid #1a1a2e' },
  hint: { fontSize: '13px', color: '#6b6b7a', margin: '0 0 10px' },
  templateLink: { color: '#2563eb', textDecoration: 'none', fontWeight: '500' },
  fileLabel: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
  fileBtn: { display: 'inline-block', padding: '8px 16px', backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500',
    color: '#374151', cursor: 'pointer' },
  fileName: { fontSize: '13px', color: '#6b6b7a' },
  textarea: { width: '100%', height: '160px', padding: '10px 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box',
    display: 'block', marginBottom: '10px', resize: 'vertical' },
  parseBtn: { padding: '8px 18px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  manualTable: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb' },
  manualTd: { padding: '6px 8px', borderBottom: '1px solid #f3f4f6' },
  cellInput: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  removeBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
    fontSize: '16px', padding: '2px 4px' },
  addRowBtn: { marginTop: '10px', padding: '7px 14px', background: 'none',
    border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
    color: '#374151', fontWeight: '500' },
  errorBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
    padding: '14px 18px', marginBottom: '16px' },
  errorText: { fontSize: '14px', color: '#dc2626', margin: 0 },
  errorList: { margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#dc2626', lineHeight: '1.8' },
  warningBox: { backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
    padding: '14px 18px', marginBottom: '16px' },
  warningText: { fontSize: '14px', color: '#92400e', margin: '0 0 4px' },
  summaryBar: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#fff',
    border: '1px solid #e5e0d5', borderRadius: '10px', padding: '16px 24px',
    flexWrap: 'wrap', fontSize: '14px' },
  saveBtn: { padding: '10px 24px', backgroundColor: '#1a1a2e', color: '#fff',
    border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '15px', cursor: 'pointer' },
  saveBtnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  cancelBtn: { padding: '10px 20px', background: 'none', border: '1px solid #d1d5db',
    borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', color: '#374151' },
  previewCount: { fontSize: '14px', color: '#6b6b7a', margin: '0 0 14px' },
  previewActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
};
